<template>
  <div>
    <div class="header">
      <div class="container-fluid">

        <!-- Filtros fila 1: Vendedor + Comprador (solo para perfiles que ven todas las empresas) -->
        <b-row v-if="puedeFiltrarEmpresas" class="align-items-end mt-3 mb-2">
          <b-col md="6">
            <label class="form-label">{{ $t('vendedor') || 'Vendedor' }}</label>
            <el-select v-model="empresaSeleccionada" :placeholder="$t('todas_las_empresas')" style="width: 100%;"
              clearable filterable :loading="loadingEmpresas" :loading-text="$t('cargando_empresas')">
              <el-option v-for="e in empresasCliente" :key="e.id" :value="e.id" :label="e.nombre" />
            </el-select>
          </b-col>
          <b-col md="6">
            <label class="form-label">{{ $t('comprador') || 'Comprador' }}</label>
            <el-select v-model="empresaCompradoraSeleccionada" :placeholder="$t('todas_las_empresas')" style="width: 100%;" clearable filterable
              :loading="loadingEmpresas" :loading-text="$t('cargando_empresas')">
              <el-option v-for="e in empresasCliente" :key="e.id" :value="e.id" :label="e.nombre" />
            </el-select>
          </b-col>
        </b-row>

        <!-- Filtros fila 2: Tipo fecha + Fechas + Buscar + Exportar -->
        <b-row class="align-items-end mt-4 mb-4">
          <b-col md="2">
            <label class="form-label">{{ $t('tipo_fecha') }}</label>
            <el-select v-model="tipoFecha" style="width: 100%;">
              <el-option v-for="op in opcionesTipoFechaI18n" :key="op.value" :value="op.value" :label="op.text" />
            </el-select>
          </b-col>
          <b-col md="2">
            <label class="form-label">{{ $t('fecha_inicio') }}</label>
            <el-date-picker v-model="fechaInicio" type="date" value-format="yyyy-MM-dd"
              :placeholder="$t('fecha_inicio')" style="width: 100%;" clearable />
          </b-col>
          <b-col md="2">
            <label class="form-label">{{ $t('fecha_fin') }}</label>
            <el-date-picker v-model="fechaFin" type="date" value-format="yyyy-MM-dd"
              :placeholder="$t('fecha_fin')" style="width: 100%;" clearable />
          </b-col>
          <b-col md="2" class="d-flex align-items-end">
            <b-button variant="primary" @click="buscarVentas" :disabled="loading" style="width:100%;">
              <b-spinner small v-if="loading" class="mr-1" />
              {{ $t('buscar') }}
            </b-button>
          </b-col>
          <b-col md="4" class="d-flex align-items-end justify-content-end" style="gap:8px;">
            <!-- Ingresar venta manual: solo perfiles de gestión (admin, ventas inex,
                 operaciones). Comprador y cliente entran a la pantalla solo a consultar. -->
            <b-button v-if="puedeEditar" variant="primary" @click="abrirIngresarVenta" :title="$t('ingresar_venta')">
              <i class="fas fa-plus mr-1"></i> {{ $t('ingresar_venta') }}
            </b-button>
            <b-button variant="success" :disabled="!ventas.length" @click="exportarExcel" :title="$t('exportar_excel')">
              <i class="fas fa-file-excel"></i>
            </b-button>
          </b-col>
        </b-row>

        <!-- Error -->
        <b-alert variant="danger" :show="!!error" dismissible @dismissed="error = ''">
          {{ error }}
        </b-alert>

        <!-- Filtro sobre resultados -->
        <div v-if="ventas.length" class="d-flex align-items-center mb-3" style="gap:12px;">
          <b-input-group style="max-width:360px;">
            <b-input-group-prepend is-text><i class="fas fa-search"></i></b-input-group-prepend>
            <b-form-input v-model="filtroTabla" :placeholder="$t('buscar') || 'Filtrar resultados...'" clearable />
          </b-input-group>
          <span class="text-muted" style="font-size:.85rem;">
            {{ ventasFiltradas.length }} / {{ ventas.length }} {{ $t('resultados') }}
          </span>
        </div>

        <!-- Tabla -->
        <div v-if="ventas.length" class="card shadow" style="margin-bottom: 60px;">
          <div class="tabla-scroll-fixed" ref="tablaVentas">
            <table class="table table-hover align-items-center mb-0">
              <thead class="thead-sticky thead-light">
                <tr>
                  <th style="width:60px;"></th>
                  <th class="th-ordenable" @click="ordenarPor('id')">
                    <input class="th-search-input" v-model="filterCol.id" @click.stop placeholder="Filtrar…" />
                    <span class="th-label">ID <i :class="iconoOrden('id')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:200px;" @click="ordenarPor('partName')">
                    <input class="th-search-input" v-model="filterCol.partName" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('descripcion') }} <i :class="iconoOrden('partName')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:160px;" @click="ordenarPor('nombreEmpresaVende')">
                    <input class="th-search-input" v-model="filterCol.nombreEmpresaVende" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('vendedor') }} <i :class="iconoOrden('nombreEmpresaVende')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:160px;" @click="ordenarPor('nombreEmpresaCompra')">
                    <input class="th-search-input" v-model="filterCol.nombreEmpresaCompra" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('comprador') }} <i :class="iconoOrden('nombreEmpresaCompra')"></i></span>
                  </th>
                  <th class="th-ordenable text-center" @click="ordenarPor('unidadDeMedida')">
                    <input class="th-search-input" v-model="filterCol.unidadDeMedida" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('uom') }} <i :class="iconoOrden('unidadDeMedida')"></i></span>
                  </th>
                  <th class="th-ordenable text-right" @click="ordenarPor('cantidad')">
                    <input class="th-search-input" v-model="filterCol.cantidad" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('cantidad_label') }} <i :class="iconoOrden('cantidad')"></i></span>
                  </th>
                  <th class="th-ordenable text-right" @click="ordenarPor('monto')">
                    <input class="th-search-input" v-model="filterCol.monto" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('monto') }} <i :class="iconoOrden('monto')"></i></span>
                  </th>
                  <th class="th-ordenable text-center" @click="ordenarPor('moneda')">
                    <input class="th-search-input" v-model="filterCol.moneda" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('moneda') }} <i :class="iconoOrden('moneda')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('fecha')">
                    <input class="th-search-input" v-model="filterCol.fecha" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('fecha_venta') }} <i :class="iconoOrden('fecha')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('fechaPublicacion')">
                    <input class="th-search-input" v-model="filterCol.fechaPublicacion" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('fecha_publicacion') }} <i :class="iconoOrden('fechaPublicacion')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('idEstado')">
                    <input class="th-search-input" v-model="filterCol.idEstado" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('estado_flujo') }} <i :class="iconoOrden('idEstado')"></i></span>
                  </th>
                  <th>
                    <input class="th-search-input" v-model="filterCol.observacion" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('observacion') }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="v in ventasPaginadas" :key="v.id">
                  <td class="text-center">
                    <b-button size="sm" variant="outline-primary" @click="verDetalle(v)"><i class="fas fa-pencil-alt"></i></b-button>
                  </td>
                  <td>{{ v.id }}</td>
                  <td>{{ v.partName}}</td>
                  <td>{{ v.nombreEmpresaVende }}</td>
                  <td>{{ v.nombreEmpresaCompra }}</td>
                  <td class="text-center">{{ v.unidadDeMedida || '-' }}</td>
                  <td class="text-right">{{ formatCantidad(v.cantidad) }}</td>
                  <td class="text-right">{{ formatMonto(v.monto) }}</td>
                  <td class="text-center">{{ v.moneda }}</td>
                  <td>{{ formatFecha(v.fecha) }}</td>
                  <td>{{ formatFecha(v.fechaPublicacion) }}</td>
                  <td>{{ estadoFlujo(v.idEstado) }}</td>
                  <td>{{ v.observacion || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Barra de paginación y scroll fija en el pie -->
        <div v-if="ventas.length" class="pagination-floating-bar">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0 text-secondary" style="font-size:.85rem;">{{ $t('items_por_pagina') || 'Items / pág.' }}</label>
            <select v-model.number="pageSize" @change="currentPage = 1" class="form-control form-control-sm" style="width:70px;">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
              <option :value="999999">Todas</option>
            </select>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <button class="btn btn-outline-secondary btn-sm" :disabled="currentPage === 1" @click="currentPage--">
              <i class="fas fa-arrow-left mr-1"></i> {{ $t('anterior') || 'Anterior' }}
            </button>
            <span style="font-weight:600;color:#6c757d;font-size:.9rem;">
              {{ $t('pagina') || 'Página' }} {{ currentPage }} {{ $t('de') || 'de' }} {{ totalPages }}
              <span class="text-muted ml-2" style="font-size:.8rem;">({{ ventasFiltradas.length }} {{ $t('resultados') || 'resultados' }})</span>
            </span>
            <button class="btn btn-outline-primary btn-sm" :disabled="currentPage >= totalPages" @click="currentPage++">
              {{ $t('siguiente') || 'Siguiente' }} <i class="fas fa-arrow-right ml-1"></i>
            </button>
          </div>
          <div class="d-flex align-items-center" style="gap:6px;">
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('left')" title="Scroll izquierda"><i class="fas fa-chevron-left"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('right')" title="Scroll derecha"><i class="fas fa-chevron-right"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('top')" title="Ir al inicio"><i class="fas fa-chevron-up"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('bottom')" title="Ir al final"><i class="fas fa-chevron-down"></i></button>
          </div>
        </div>

        <div v-else-if="!loading && buscado" class="text-center text-muted mt-5">
          <i class="fas fa-inbox fa-3x mb-3"></i>
          <p>{{ $t('no_ventas_periodo') }}</p>
        </div>

      </div>
    </div>

    <!-- Modal detalle transacción -->
    <b-modal v-model="modalVisible" :title="$t('detalle_transaccion')" size="xl" hide-footer @hidden="onModalHidden">
      <div v-if="ventaSeleccionada" class="detalle-modal">

        <!-- Barra de acciones edición -->
        <div v-if="puedeEditar" class="d-flex justify-content-end mb-2">
          <b-button v-if="!modoEdicion" variant="outline-primary" size="sm" @click="activarEdicion">
            <i class="fas fa-pencil-alt mr-1"></i> {{ $t('editar') }}
          </b-button>
          <template v-else>
            <b-button variant="outline-secondary" size="sm" class="mr-2" @click="cancelarEdicion">
              <i class="fas fa-times mr-1"></i> {{ $t('cancelar') }}
            </b-button>
            <b-button v-if="puedeEditar" variant="primary" size="sm" :disabled="guardandoVenta" @click="guardarVenta">
              <b-spinner v-if="guardandoVenta" small class="mr-1" />
              <i v-else class="fas fa-save mr-1"></i> {{ $t('guardar') }}
            </b-button>
          </template>
        </div>
        <b-alert variant="danger" :show="!!errorEdicionVenta" dismissible @dismissed="errorEdicionVenta = ''">
          {{ errorEdicionVenta }}
        </b-alert>

        <!-- Datos generales -->
        <div class="row">
          <div class="col-md-6">
            <div class="detalle-item">
              <span class="detalle-label">ID</span>
              <span class="detalle-valor">{{ ventaSeleccionada.id }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('descripcion') }}</span>
              <span class="detalle-valor">{{ ventaSeleccionada.partName || '-' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('vendedor') }}</span>
              <span class="detalle-valor">{{ ventaSeleccionada.nombreEmpresaVende || '-' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('comprador') }}</span>
              <span class="detalle-valor">{{ ventaSeleccionada.nombreEmpresaCompra || '-' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('unidad_medida') }}</span>
              <span class="detalle-valor">{{ ventaSeleccionada.unidadDeMedida || '-' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('cantidad_label') }}</span>
              <span v-if="!modoEdicion" class="detalle-valor">{{ formatCantidad(ventaSeleccionada.cantidad) }}</span>
              <b-form-input v-else v-model.number="editForm.cantidad" type="number" size="sm" min="1" style="max-width:160px" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('monto') }}</span>
              <span v-if="!modoEdicion" class="detalle-valor">{{ formatMonto(ventaSeleccionada.monto) }}</span>
              <input v-else type="text" class="form-control form-control-sm" style="max-width:160px"
                :value="montoEditFormateado"
                @keypress="onMontoEditKeypress"
                @input="e => onMontoEditInput(e.target.value)"
                @blur="e => onMontoEditBlur(e.target.value)" />
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('moneda') }}</span>
              <span v-if="!modoEdicion" class="detalle-valor">{{ ventaSeleccionada.moneda || '-' }}</span>
              <b-form-select v-else v-model="editForm.moneda" :options="[{value:'USD',text:'USD'},{value:'CLP',text:'CLP'},{value:'EUR',text:'EUR'}]" size="sm" style="max-width:120px" />
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('fecha_venta') }}</span>
              <span v-if="!modoEdicion" class="detalle-valor">{{ formatFecha(ventaSeleccionada.fecha) }}</span>
              <b-form-input v-else v-model="editForm.fecha" type="date" size="sm" style="max-width:160px" />
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('fecha_publicacion') }}</span>
              <span class="detalle-valor">{{ formatFecha(ventaSeleccionada.fechaPublicacion) }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('estado_flujo') }}</span>
              <span v-if="!modoEdicion" class="detalle-valor">{{ estadoFlujo(ventaSeleccionada.idEstado) }}</span>
              <b-form-select v-else v-model.number="editForm.idEstado" :options="opcionesEstado" size="sm" style="max-width:160px" />
            </div>
            <div class="detalle-item">
              <span class="detalle-label">{{ $t('observacion') }}</span>
              <span v-if="!modoEdicion" class="detalle-valor">{{ ventaSeleccionada.observacion || '-' }}</span>
              <b-form-textarea v-else v-model="editForm.observacion" rows="2" size="sm" style="max-width:300px" />
            </div>
          </div>
        </div>

        <hr class="my-3" />

        <!-- Pagos + Archivos en fila -->
        <div class="row">
          <!-- Sección Pagos -->
          <div class="col-md-6 seccion-col">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="archivos-titulo mb-0">{{ $t('pagos') }}</h6>
              <b-button v-if="puedeEditar" variant="primary" size="sm" @click="abrirNuevoPago">
                <i class="fas fa-plus mr-1"></i> {{ $t('nuevo_pago') }}
              </b-button>
            </div>

            <b-alert variant="danger" :show="!!errorPagos" dismissible @dismissed="errorPagos = ''">
              {{ errorPagos }}
            </b-alert>

            <div v-if="loadingPagos" class="text-center text-muted py-3">
              <b-spinner small /> {{ $t('cargando_pagos') }}
            </div>
            <div v-else-if="pagos.length === 0" class="text-muted small text-center py-3">
              {{ $t('sin_pagos') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover table-sm align-items-center mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>{{ $t('fecha') }}</th>
                    <th class="text-right">{{ $t('monto') }}</th>
                    <th class="text-center">{{ $t('moneda') }}</th>
                    <th>{{ $t('observacion') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in pagos" :key="p.id">
                    <td><small>{{ formatFecha(p.fecha) }}</small></td>
                    <td class="text-right">{{ formatMonto(p.monto) }}</td>
                    <td class="text-center">{{ p.moneda }}</td>
                    <td>
                      <span v-if="p.observacion" class="text-truncate d-inline-block" style="max-width: 100px;">
                        {{ p.observacion }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="text-right" style="white-space: nowrap;">
                      <b-button v-if="puedeEditar" size="sm" variant="outline-secondary" @click="abrirEditarPago(p)" class="mr-1">
                        <i class="fas fa-pencil-alt"></i>
                      </b-button>
                      <!-- Adjuntar comprobante: escritura. Ver los adjuntos sigue
                           disponible para los perfiles de solo lectura. -->
                      <b-button v-if="puedeEditar" size="sm" variant="outline-primary" @click="abrirAdjuntoPago(p)" class="mr-1">
                        <i class="fas fa-file"></i>
                      </b-button>
                      <b-button size="sm" variant="outline-info" @click="verAdjuntosPago(p)">
                        {{ $t('ver') }}
                      </b-button>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="font-weight-bold">
                    <td>{{ $t('total_label') }}</td>
                    <td class="text-right">{{ formatMonto(totalPagos) }}</td>
                    <td colspan="3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Sección Archivos Adjuntos -->
          <div class="col-md-6 seccion-col seccion-col--border">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="archivos-titulo mb-0">{{ $t('archivos_adjuntos') }}</h6>
              <b-button v-if="puedeEditar" variant="primary" size="sm" @click="modalNuevoAdjunto = true">
                <i class="fas fa-plus mr-1"></i> {{ $t('nuevo_adjunto') }}
              </b-button>
            </div>

            <b-alert variant="danger" :show="!!errorArchivos" dismissible @dismissed="errorArchivos = ''">
              {{ errorArchivos }}
            </b-alert>

            <div v-if="loadingArchivos" class="text-center text-muted py-3">
              <b-spinner small /> {{ $t('cargando_archivos') }}
            </div>
            <div v-else-if="archivos.length === 0" class="text-muted small text-center py-3">
              {{ $t('sin_archivos_adjuntos') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover table-sm align-items-center mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>{{ $t('proceso') }}</th>
                    <th class="text-center">{{ $t('adjuntos') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="grupo in archivosAgrupados" :key="grupo.idTipo">
                    <td>
                      <span v-if="grupo.codigo" class="text-muted mr-1">{{ grupo.codigo }}.</span>
                      {{ grupo.nombre }}
                    </td>
                    <td class="text-center">
                      <b-badge variant="secondary">{{ grupo.archivos.length }}</b-badge>
                    </td>
                    <td class="text-right">
                      <b-button size="sm" variant="outline-primary" @click="verDetalleArchivos(grupo)">
                        {{ $t('ver') }}
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Modal Ingresar Venta (manual) -->
    <b-modal
      v-model="modalIngresarVenta"
      :title="$t('ingresar_venta')"
      size="xl"
      no-enforce-focus
      hide-footer
      @hidden="resetVentaManual"
    >
      <div class="row">
        <div class="col-md-4">
          <b-form-group :label="$t('empresa_vende')">
            <el-select v-model="nuevaVentaManual.idEmpresaVende" :placeholder="$t('seleccione_empresa_vende')"
              style="width: 100%;" clearable filterable :loading="loadingEmpresas">
              <el-option v-for="e in empresasCliente" :key="e.id" :value="e.id" :label="e.nombre" />
            </el-select>
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('empresa_compra')">
            <el-select v-model="nuevaVentaManual.idEmpresaCompra" :placeholder="$t('seleccione_empresa_compra')"
              style="width: 100%;" clearable filterable :loading="loadingEmpresas">
              <el-option v-for="e in empresasCliente" :key="e.id" :value="e.id" :label="e.nombre" />
            </el-select>
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('fecha_venta')">
            <b-form-input v-model="nuevaVentaManual.fechaVenta" type="date" />
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <b-form-group :label="$t('cantidad_label')">
            <b-form-input v-model.number="nuevaVentaManual.cantidad" type="number" min="1" />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('moneda')">
            <b-form-input :value="nuevaVentaManual.moneda" disabled />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('monto_venta')">
            <input type="text" class="form-control" :value="montoVentaManualFormateado"
              @keypress="onMontoEditKeypress"
              @input="e => onVentaManualMontoInput(e.target.value, 'monto', 'montoVentaManualFormateado')"
              @blur="e => onVentaManualMontoBlur(e.target.value, 'monto', 'montoVentaManualFormateado')" />
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <b-form-group :label="$t('label_sku')">
            <b-form-input v-model="nuevaVentaManual.sku" maxlength="30" />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('label_brand_name')">
            <b-form-input v-model="nuevaVentaManual.brandName" maxlength="400" />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('label_part_name')">
            <b-form-input v-model="nuevaVentaManual.partName" maxlength="600" />
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <b-form-group :label="$t('part_number')">
            <b-form-input v-model="nuevaVentaManual.partNumber" maxlength="600" />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('label_detalles')">
            <b-form-textarea v-model="nuevaVentaManual.detalles" rows="2" max-rows="4" />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('categoria_origen')">
            <el-select v-model="nuevaVentaManual.categoriaOrigen" :placeholder="$t('seleccione_categoria')"
              style="width: 100%;" clearable filterable :loading="loadingCategorias">
              <el-option v-for="c in opcionesCategorias" :key="c.value" :value="c.value" :label="c.label" />
            </el-select>
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <b-form-group :label="$t('categoria_inexlink')">
            <el-select v-model="nuevaVentaManual.categoriaInexlink" :placeholder="$t('seleccione_categoria')"
              style="width: 100%;" clearable filterable :loading="loadingCategorias">
              <el-option v-for="c in opcionesCategorias" :key="c.value" :value="c.value" :label="c.label" />
            </el-select>
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('label_condicion')">
            <el-select v-model="nuevaVentaManual.idCondicion" :placeholder="$t('seleccione_condicion')"
              style="width: 100%;" clearable filterable :loading="loadingCondiciones">
              <el-option v-for="c in opcionesCondicion" :key="c.value" :value="c.value" :label="c.label" />
            </el-select>
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('unidad_medida')">
            <b-form-input v-model="nuevaVentaManual.unidadDeMedida" maxlength="30" />
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <b-form-group :label="$t('precio_libro')">
            <input type="text" class="form-control" :value="precioLibroFormateado"
              @keypress="onMontoEditKeypress"
              @input="e => onVentaManualMontoInput(e.target.value, 'precio', 'precioLibroFormateado')"
              @blur="e => onVentaManualMontoBlur(e.target.value, 'precio', 'precioLibroFormateado')" />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group :label="$t('observacion')">
            <b-form-textarea v-model="nuevaVentaManual.observacion" rows="2" max-rows="4" maxlength="100" />
          </b-form-group>
        </div>
        <div class="col-md-4 d-flex align-items-end justify-content-end" style="gap:8px;">
          <b-button variant="secondary" @click="modalIngresarVenta = false">{{ $t('cancelar') }}</b-button>
          <b-button v-if="puedeEditar" variant="primary" :disabled="guardandoVentaManual" @click="guardarVentaManual">
            <b-spinner small v-if="guardandoVentaManual" class="mr-1" />
            {{ $t('guardar') }}
          </b-button>
        </div>
      </div>

      <b-alert variant="danger" :show="!!errorVentaManual" dismissible @dismissed="errorVentaManual = ''">
        {{ errorVentaManual }}
      </b-alert>
    </b-modal>

    <!-- Modal Nuevo / Editar Pago -->
    <b-modal
      v-model="modalNuevoPago"
      :title="pagoEditandoId ? $t('editar_pago') : $t('nuevo_pago')"
      size="md"
      no-enforce-focus
      @hidden="resetNuevoPago"
    >
      <b-form-group :label="$t('fecha')">
        <el-date-picker v-model="nuevoPago.fecha" type="date" value-format="yyyy-MM-dd"
          :placeholder="$t('fecha')" style="width: 100%;" clearable />
      </b-form-group>

      <b-form-group :label="$t('moneda')">
        <el-select v-model="nuevoPago.moneda" style="width: 100%;">
          <el-option value="USD" label="USD" />
          <el-option value="CLP" label="CLP" />
          <el-option value="EUR" label="EUR" />
        </el-select>
      </b-form-group>

      <b-form-group :label="$t('monto')">
        <b-form-input
          v-model="nuevoPago.monto"
          type="number"
          min="0"
          step="0.01"
          placeholder="0.00"
        />
      </b-form-group>

      <b-form-group :label="$t('observacion')">
        <b-form-textarea
          v-model="nuevoPago.observacion"
          :placeholder="$t('comentario_opcional')"
          rows="3"
          max-rows="5"
        />
      </b-form-group>

      <b-alert variant="danger" :show="!!errorPagos" dismissible @dismissed="errorPagos = ''">
        {{ errorPagos }}
      </b-alert>

      <template #modal-footer>
        <b-button variant="secondary" @click="modalNuevoPago = false">{{ $t('cancelar') }}</b-button>
        <b-button v-if="puedeEditar"
          variant="primary"
          :disabled="!nuevoPago.fecha || !nuevoPago.monto || guardandoPago"
          @click="guardarPago"
        >
          <b-spinner small v-if="guardandoPago" class="mr-1" />
          {{ $t('guardar') }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal Ver Adjuntos de Pago -->
    <b-modal
      v-model="modalVerAdjuntosPago"
      :title="pagoSeleccionadoVer ? `${$t('adjuntos_pago')} #${pagoSeleccionadoVer.id}` : $t('adjuntos_pago')"
      size="lg"
      no-enforce-focus
      hide-footer
    >
      <div v-if="loadingAdjuntosPago" class="text-center text-muted py-3">
        <b-spinner small /> {{ $t('cargando_adjuntos') }}
      </div>
      <div v-else-if="adjuntosPago.length === 0" class="text-muted small text-center py-3">
        {{ $t('sin_adjuntos_pago') }}
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover table-sm align-items-center mb-0">
          <thead class="thead-light">
            <tr>
              <th>{{ $t('archivo') }}</th>
              <th>{{ $t('comentario') }}</th>
              <th>{{ $t('fecha') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="archivo in adjuntosPago" :key="archivo.id">
              <td>
                <i class="fas fa-paperclip mr-1 text-muted"></i>{{ archivo.archivo || nombreArchivo(archivo.url) }}
              </td>
              <td>
                <span v-if="archivo.observacion" class="d-inline-flex align-items-center">
                  <span class="text-truncate d-inline-block" style="max-width: 160px;">{{ archivo.observacion.length > 40 ? archivo.observacion.substring(0, 40) + '…' : archivo.observacion }}</span>
                  <i
                    class="fas fa-info-circle ml-1 text-info"
                    v-b-tooltip.hover.top="archivo.observacion"
                    style="cursor: pointer; flex-shrink: 0;"
                  ></i>
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td><small class="text-muted">{{ formatFecha(archivo.fechaCreacion) }}</small></td>
              <td class="text-right">
                <b-button variant="outline-primary" size="sm" :href="archivo.url" target="_blank" rel="noopener" class="mr-1">
                  <i class="fas fa-eye mr-1"></i>{{ $t('ver') }}
                </b-button>
                <b-button v-if="puedeEditar" variant="outline-danger" size="sm" @click="eliminarAdjuntoPago(archivo.id)">
                  <i class="fas fa-trash"></i>
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-alert variant="danger" :show="!!errorAdjuntosPagoVer" dismissible class="mt-3 mb-0" @dismissed="errorAdjuntosPagoVer = ''">
        {{ errorAdjuntosPagoVer }}
      </b-alert>
    </b-modal>

    <!-- Modal Adjunto de Pago -->
    <b-modal
      v-model="modalAdjuntoPago"
      :title="$t('adjunto_pago')"
      size="md"
      no-enforce-focus
      @hidden="resetAdjuntoPago"
    >
      <b-form-group :label="$t('archivo')">
        <b-form-file
          v-model="archivoPago"
          :placeholder="$t('seleccione_archivo')"
          :browse-text="$t('buscar')"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        />
      </b-form-group>

      <b-form-group :label="$t('comentario')">
        <b-form-textarea
          v-model="comentarioPago"
          :placeholder="$t('comentario_opcional')"
          rows="3"
          max-rows="5"
        />
      </b-form-group>

      <b-alert variant="danger" :show="!!errorAdjuntoPago" dismissible @dismissed="errorAdjuntoPago = ''">
        {{ errorAdjuntoPago }}
      </b-alert>

      <template #modal-footer>
        <b-button variant="secondary" @click="modalAdjuntoPago = false">{{ $t('cancelar') }}</b-button>
        <b-button v-if="puedeEditar"
          variant="primary"
          :disabled="!archivoPago || subiendoAdjuntoPago"
          @click="subirAdjuntoPago"
        >
          <b-spinner small v-if="subiendoAdjuntoPago" class="mr-1" />
          {{ $t('subir') }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal Nuevo Adjunto -->
    <b-modal
      v-model="modalNuevoAdjunto"
      :title="$t('nuevo_adjunto')"
      size="md"
      no-enforce-focus
      @hidden="resetNuevoAdjunto"
    >
      <b-form-group :label="$t('tipo_adjunto')">
        <b-form-select
          v-model="tipoAdjuntoSeleccionado"
          :options="opcionesTipoAdjunto"
        >
          <template #first>
            <b-form-select-option :value="null">— {{ $t('seleccione_tipo') }} —</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-group :label="$t('archivo')">
        <b-form-file
          v-model="archivoNuevo"
          :placeholder="$t('seleccione_archivo')"
          :browse-text="$t('buscar')"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        />
      </b-form-group>

      <b-form-group :label="$t('comentario')">
        <b-form-textarea
          v-model="observacionNuevo"
          :placeholder="$t('comentario_opcional')"
          rows="3"
          max-rows="5"
        />
      </b-form-group>

      <b-alert variant="danger" :show="!!errorArchivos" dismissible @dismissed="errorArchivos = ''">
        {{ errorArchivos }}
      </b-alert>

      <template #modal-footer>
        <b-button variant="secondary" @click="modalNuevoAdjunto = false">{{ $t('cancelar') }}</b-button>
        <b-button v-if="puedeEditar"
          variant="primary"
          :disabled="!archivoNuevo || !tipoAdjuntoSeleccionado || subiendoArchivos"
          @click="subirArchivos"
        >
          <b-spinner small v-if="subiendoArchivos" class="mr-1" />
          {{ $t('subir') }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal detalle archivos por proceso -->
    <b-modal
      v-model="modalDetalleArchivos"
      :title="procesoSeleccionado ? `${procesoSeleccionado.codigo ? procesoSeleccionado.codigo + '. ' : ''}${procesoSeleccionado.nombre}` : 'Archivos'"
      size="lg"
      no-enforce-focus
      hide-footer
    >
      <div v-if="procesoSeleccionado">
        <div class="table-responsive">
          <table class="table table-hover table-sm align-items-center mb-0">
            <thead class="thead-light">
              <tr>
                <th>{{ $t('archivo') }}</th>
                <th>{{ $t('comentario') }}</th>
                <th>{{ $t('fecha') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="archivo in procesoSeleccionado.archivos" :key="archivo.id">
                <td>
                  <i class="fas fa-paperclip mr-1 text-muted"></i>{{ archivo.archivo || nombreArchivo(archivo.url) }}
                </td>
                <td>
                  <span v-if="archivo.observacion" class="d-inline-flex align-items-center">
                    <span class="text-truncate d-inline-block" style="max-width: 160px;">{{ archivo.observacion.length > 40 ? archivo.observacion.substring(0, 40) + '…' : archivo.observacion }}</span>
                    <i
                      class="fas fa-info-circle ml-1 text-info"
                      v-b-tooltip.hover.top="archivo.observacion"
                      style="cursor: pointer; flex-shrink: 0;"
                    ></i>
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td><small class="text-muted">{{ formatFecha(archivo.fechaCreacion) }}</small></td>
                <td class="text-right">
                  <b-button variant="outline-primary" size="sm" :href="archivo.url" target="_blank" rel="noopener" class="mr-1">
                    <i class="fas fa-eye mr-1"></i>{{ $t('ver') }}
                  </b-button>
                  <b-button v-if="puedeEditar" variant="outline-danger" size="sm" @click="eliminarArchivo(archivo.id, procesoSeleccionado)">
                    <i class="fas fa-trash"></i>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <b-alert variant="danger" :show="!!errorDetalle" dismissible class="mt-3 mb-0" @dismissed="errorDetalle = ''">
          {{ errorDetalle }}
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ventasService from '@/api/ventasService';
import materialService from '@/api/materialService';
import utilsService from '@/api/utilsService';
import archivoAdjuntoService from '@/api/archivoAdjuntoService';
import ventaTipoAdjuntoService from '@/api/ventaTipoAdjuntoService';
import ventaPagoService from '@/api/ventaPagoService';
import diccionarioService from '@/api/diccionarioService';
import { Select, Option, DatePicker } from 'element-ui';
import * as XLSX from 'xlsx';

export default {
  name: 'VisorTransacciones',

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
  },

  data() {
    const hoy = new Date().toISOString().slice(0, 10);
    return {
      lang: localStorage.getItem('language') || 'es',
      fechaInicio: '2015-01-01',
      fechaFin: hoy,
      tipoFecha: 'FV',
      opcionesTipoFecha: [
        { value: 'FV', text: 'Fecha Venta' },
        { value: 'FP', text: 'Fecha Publicación' },
      ],
      ventas: [],
      loading: false,
      buscado: false,
      error: '',
      ordenColumna: null,
      ordenDireccion: 'asc',
      empresasCliente: [],
      empresaSeleccionada: null,
      empresaCompradoraSeleccionada: null,
      loadingEmpresas: false,
      esCliente: false,
      esComprador: false,
      filtroTabla: '',
      filterCol: {
        id: '',
        partName: '',
        nombreEmpresaVende: '',
        nombreEmpresaCompra: '',
        unidadDeMedida: '',
        cantidad: '',
        monto: '',
        moneda: '',
        fecha: '',
        fechaPublicacion: '',
        idEstado: '',
        observacion: '',
      },
      currentPage: 1,
      pageSize: 50,
      modalVisible: false,
      ventaSeleccionada: null,
      archivos: [],
      loadingArchivos: false,
      archivosSeleccionados: [],
      subiendoArchivos: false,
      errorArchivos: '',
      modalNuevoAdjunto: false,
      modalDetalleArchivos: false,
      errorDetalle: '',
      procesoSeleccionado: null,
      tiposAdjunto: [],
      tipoAdjuntoSeleccionado: null,
      archivoNuevo: null,
      observacionNuevo: '',
      // Pagos
      pagos: [],
      loadingPagos: false,
      errorPagos: '',
      modalNuevoPago: false,
      guardandoPago: false,
      nuevoPago: { fecha: '', moneda: 'USD', monto: '', observacion: '' },
      pagoEditandoId: null,
      // Adjunto de pago (subir)
      modalAdjuntoPago: false,
      pagoSeleccionadoAdjunto: null,
      archivoPago: null,
      comentarioPago: '',
      subiendoAdjuntoPago: false,
      errorAdjuntoPago: '',
      // Adjunto de pago (ver)
      modalVerAdjuntosPago: false,
      pagoSeleccionadoVer: null,
      adjuntosPago: [],
      loadingAdjuntosPago: false,
      errorAdjuntosPagoVer: '',
      // Edición de transacción
      modoEdicion: false,
      editForm: { cantidad: '', monto: '', moneda: 'USD', fecha: '', observacion: '', idEstado: 1 },
      montoEditFormateado: '',
      guardandoVenta: false,
      errorEdicionVenta: '',
      // Ingresar Venta (manual, vía SP SpVentaManual)
      modalIngresarVenta: false,
      guardandoVentaManual: false,
      errorVentaManual: '',
      nuevaVentaManual: this.ventaManualVacia(),
      montoVentaManualFormateado: '',
      precioLibroFormateado: '',
      categorias: [],
      loadingCategorias: false,
      condiciones: [],
      loadingCondiciones: false,
    };
  },

  async mounted() {
    this.$store.dispatch('setSidebarCollapsed', false);
    this._scrollHandler = this._onWindowScrollVentas.bind(this);
    window.addEventListener('scroll', this._scrollHandler, { passive: true });

    this.loadingEmpresas = true;
    try {
      const resp = await materialService.getEmpresasAsociadas();
      let data = resp;
      if (resp && typeof resp === 'object') {
        if ('data' in resp) data = resp.data;
        else if ('items' in resp) data = resp.items;
      }
      this.empresasCliente = Array.isArray(data) ? data : [];
      if (!this.empresasCliente.length) {
        console.warn('[VisorTransacciones] getEmpresasAsociadas devolvió vacío. Resp:', resp);
      }
      const rol = (utilsService.getRol() || '').toLowerCase().trim();
      this.esCliente = (rol === 'cliente');
      this.esComprador = (rol === 'comprador');
      if (this.esCliente && this.empresasCliente.length) {
        this.empresaSeleccionada = this.empresasCliente[0].id;
      }
      if (this.esComprador && this.empresasCliente.length) {
        this.empresaCompradoraSeleccionada = this.empresasCliente[0].id;
      }
    } catch (err) {
      console.error('[VisorTransacciones] Error cargando empresas:', err && err.response ? err.response.status : '', err && err.response ? err.response.data : err && err.message);
    } finally {
      this.loadingEmpresas = false;
    }
    this.buscarVentas();
  },

  beforeDestroy() {
    this.$store.dispatch('setSidebarCollapsed', false);
    window.removeEventListener('scroll', this._scrollHandler);
  },

  watch: {
    filtroTabla() { this.currentPage = 1; },
    filterCol: { deep: true, handler() { this.currentPage = 1; } },
  },

  computed: {
    rol() {
      return (utilsService.getRol() || '').toLowerCase().trim();
    },
    puedeFiltrarEmpresas() {
      return this.esSoloLectura || this.rol === 'admin' || this.rol === 'ventas inex' || this.rol === 'operaciones';
    },
    nombreEmpresaSeleccionada() {
      const e = (this.empresasCliente || []).find(x => x.id === this.empresaSeleccionada);
      return e ? (e.nombre || '') : '';
    },
    // Única puerta de escritura de la pantalla: ventas, pagos y adjuntos son de
    // los perfiles de gestión. Comprador y cliente entran solo a consultar, y
    // `puedeEscribir` deja fuera además a los perfiles de solo lectura.
    puedeEditar() {
      return this.puedeEscribir
        && (this.rol === 'admin' || this.rol === 'ventas inex' || this.rol === 'operaciones');
    },
    opcionesTipoFechaI18n() {
      return [
        { value: 'FV', text: this.$t('fecha_venta') },
        { value: 'FP', text: this.$t('fecha_publicacion') },
      ];
    },
    opcionesEstado() {
      return [
        { value: 1, text: 'Por Activar' },
        { value: 2, text: 'Publicado' },
        { value: 3, text: 'Adjudicado' },
        { value: 4, text: 'Cancelado' },
        { value: 5, text: 'Completado' },
      ];
    },
    archivosAgrupados() {
      const mapa = {};
      for (const a of this.archivos) {
        const key = a.idTipoAdjuntoVenta !== null && a.idTipoAdjuntoVenta !== undefined ? a.idTipoAdjuntoVenta : 'sin-tipo';
        if (!mapa[key]) {
          mapa[key] = {
            idTipo: key,
            codigo: a.codigoTipoAdjuntoVenta || '',
            nombre: a.nombreTipoAdjuntoVenta || 'Sin tipo',
            archivos: [],
          };
        }
        mapa[key].archivos.push(a);
      }
      return Object.values(mapa).sort((a, b) =>
        (a.codigo || '').localeCompare(b.codigo || '', undefined, { numeric: true })
      );
    },
    opcionesTipoAdjunto() {
      return [...this.tiposAdjunto]
        .sort((a, b) => (a.codigo || '').localeCompare(b.codigo || '', undefined, { numeric: true }))
        .map(t => ({
          value: t.id,
          text: t.codigo ? `${t.codigo}. ${t.nombre}` : t.nombre,
        }));
    },
    totalPagos() {
      return this.pagos.reduce((sum, p) => sum + (Number(p.monto) || 0), 0);
    },
    idiomaActual() {
      return (localStorage.getItem('language') || 'es').toLowerCase();
    },
    opcionesCategorias() {
      const lang = this.idiomaActual;
      const vistos = new Set();
      const opciones = [];
      for (const c of this.categorias) {
        const categoria = c.Categoria || c.categoria || '';
        const categoriaIngles = c.CategoriaIngles || c.categoriaIngles || '';
        if (!categoria || vistos.has(categoria)) continue;
        vistos.add(categoria);
        opciones.push({ value: categoria, label: lang === 'en' && categoriaIngles ? categoriaIngles : categoria });
      }
      return opciones.sort((a, b) => a.label.localeCompare(b.label, 'es'));
    },
    opcionesCondicion() {
      const lang = this.idiomaActual;
      return this.condiciones
        .map(c => {
          const id = c.Id != null ? c.Id : c.id;
          const nombre = c.Nombre || c.nombre || '';
          const nombreIngles = c.NombreIngles || c.nombreIngles || '';
          return { value: id, label: lang === 'en' && nombreIngles ? nombreIngles : nombre };
        })
        .sort((a, b) => a.label.localeCompare(b.label, 'es'));
    },
    ventasFiltradas() {
      const q = (this.filtroTabla || '').toLowerCase().trim();
      const fc = this.filterCol;
      return this.ventas.filter(v => {
        if (q && !(
          String(v.id              || '').includes(q) ||
          (v.partName              || '').toLowerCase().includes(q) ||
          (v.nombreEmpresaVende    || '').toLowerCase().includes(q) ||
          (v.nombreEmpresaCompra   || '').toLowerCase().includes(q) ||
          (v.moneda                || '').toLowerCase().includes(q) ||
          (v.unidadDeMedida        || '').toLowerCase().includes(q) ||
          (v.observacion           || '').toLowerCase().includes(q) ||
          this.estadoFlujo(v.idEstado).toLowerCase().includes(q) ||
          this.formatFecha(v.fecha).toLowerCase().includes(q) ||
          this.formatFecha(v.fechaPublicacion).toLowerCase().includes(q) ||
          String(v.cantidad        || '').includes(q) ||
          String(v.monto           || '').includes(q)
        )) return false;
        if (fc.id             && !String(v.id || '').toLowerCase().includes(fc.id.toLowerCase())) return false;
        if (fc.partName       && !(v.partName || '').toLowerCase().includes(fc.partName.toLowerCase())) return false;
        if (fc.nombreEmpresaVende  && !(v.nombreEmpresaVende || '').toLowerCase().includes(fc.nombreEmpresaVende.toLowerCase())) return false;
        if (fc.nombreEmpresaCompra && !(v.nombreEmpresaCompra || '').toLowerCase().includes(fc.nombreEmpresaCompra.toLowerCase())) return false;
        if (fc.unidadDeMedida    && !(v.unidadDeMedida || '').toLowerCase().includes(fc.unidadDeMedida.toLowerCase())) return false;
        if (fc.cantidad          && !this.formatCantidad(v.cantidad).includes(fc.cantidad)) return false;
        if (fc.monto             && !this.formatMonto(v.monto).includes(fc.monto)) return false;
        if (fc.moneda            && !(v.moneda || '').toLowerCase().includes(fc.moneda.toLowerCase())) return false;
        if (fc.fecha             && !this.formatFecha(v.fecha).includes(fc.fecha)) return false;
        if (fc.fechaPublicacion  && !this.formatFecha(v.fechaPublicacion).includes(fc.fechaPublicacion)) return false;
        if (fc.idEstado          && !this.estadoFlujo(v.idEstado).toLowerCase().includes(fc.idEstado.toLowerCase())) return false;
        if (fc.observacion       && !(v.observacion || '').toLowerCase().includes(fc.observacion.toLowerCase())) return false;
        return true;
      });
    },
    totalPages() {
      if (this.pageSize >= 999999) return 1;
      return Math.max(1, Math.ceil(this.ventasOrdenadas.length / this.pageSize));
    },
    ventasPaginadas() {
      if (this.pageSize >= 999999) return this.ventasOrdenadas;
      const start = (this.currentPage - 1) * this.pageSize;
      return this.ventasOrdenadas.slice(start, start + this.pageSize);
    },
    ventasOrdenadas() {
      if (!this.ordenColumna) return this.ventasFiltradas;

      const lista = [...this.ventasFiltradas];
      lista.sort((a, b) => {
        let valorA = a[this.ordenColumna];
        let valorB = b[this.ordenColumna];

        if (valorA === null || valorA === undefined) valorA = '';
        if (valorB === null || valorB === undefined) valorB = '';

        if (typeof valorA === 'number' && typeof valorB === 'number') {
          return this.ordenDireccion === 'asc' ? valorA - valorB : valorB - valorA;
        }

        valorA = String(valorA).toLowerCase();
        valorB = String(valorB).toLowerCase();

        if (valorA < valorB) return this.ordenDireccion === 'asc' ? -1 : 1;
        if (valorA > valorB) return this.ordenDireccion === 'asc' ? 1 : -1;
        return 0;
      });

      return lista;
    },
  },

  methods: {
    ordenarPor(columna) {
      if (this.ordenColumna === columna) {
        this.ordenDireccion = this.ordenDireccion === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumna = columna;
        this.ordenDireccion = 'asc';
      }
      this.currentPage = 1;
    },

    obtenerIconoOrden(columna) {
      if (this.ordenColumna !== columna) return '↕';
      return this.ordenDireccion === 'asc' ? '↑' : '↓';
    },

    iconoOrden(columna) {
      if (this.ordenColumna !== columna) return 'fas fa-sort text-muted';
      return this.ordenDireccion === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    scrollTabla(dir) {
      const el = this.$refs.tablaVentas;
      const px = 300;
      if (dir === 'left')   el && el.scrollBy({ left: -px, behavior: 'smooth' });
      if (dir === 'right')  el && el.scrollBy({ left:  px, behavior: 'smooth' });
      if (dir === 'top')    window.scrollTo({ top: 0, behavior: 'smooth' });
      if (dir === 'bottom') window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    },

    _onWindowScrollVentas() {
      const cont = this.$refs.tablaVentas;
      if (!cont) return;
      const thead = cont.querySelector('thead.thead-sticky');
      if (!thead) return;
      const top = cont.getBoundingClientRect().top;
      thead.style.transform = top < 0 ? `translateY(${-top}px)` : '';
    },

    async buscarVentas() {
      if (!this.fechaInicio || !this.fechaFin) {
        this.error = 'Debe seleccionar ambas fechas.';
        return;
      }
      this.loading = true;
      this.error = '';
      this.ventas = [];
      this.ordenColumna = null;
      this.filtroTabla = '';
      this.filterCol = { id: '', partName: '', nombreEmpresaVende: '', nombreEmpresaCompra: '', unidadDeMedida: '', cantidad: '', monto: '', moneda: '', fecha: '', fechaPublicacion: '', idEstado: '', observacion: '' };
      this.currentPage = 1;
      try {
        this.ventas = await ventasService.getVentasByFecha(this.fechaInicio, this.fechaFin, this.empresaSeleccionada, this.tipoFecha, this.empresaCompradoraSeleccionada);
        this.buscado = true;
      } catch (e) {
        this.error = (e && e.response && e.response.data && e.response.data.message) || 'Error al obtener las ventas.';
      } finally {
        this.loading = false;
      }
    },

    formatCantidad(cantidad) {
      if (cantidad == null) return '-';
      return Number(cantidad).toLocaleString('es-CL');
    },

    formatMonto(monto) {
      if (monto == null) return '-';
      const numero = Number(monto);
      if (Number.isNaN(numero)) return '-';
      const locale = this.lang === 'en' ? 'en-US' : 'es-CL';
      return numero.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    },

    parseMontoLocale(val) {
      if (val === null || val === undefined || val === '') return null;
      let s = String(val).trim();
      s = this.lang === 'en' ? s.replace(/,/g, '') : s.replace(/\./g, '').replace(',', '.');
      const n = parseFloat(s);
      return Number.isNaN(n) ? null : n;
    },

    onMontoEditKeypress(e) {
      // El teclado numérico solo tiene ".", pero en español el decimal es ",".
      if (this.lang !== 'en' && e.key === '.') {
        e.preventDefault();
        const input = e.target;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const value = input.value;
        const newValue = value.slice(0, start) + ',' + value.slice(end);
        input.value = newValue;
        input.selectionStart = input.selectionEnd = start + 1;
        input.dispatchEvent(new Event('input'));
      }
    },

    onMontoEditInput(value) {
      // Mientras el usuario escribe se deja el texto tal cual (sin reformatear en cada tecla).
      this.montoEditFormateado = value;
      const parsed = this.parseMontoLocale(value);
      this.editForm.monto = parsed != null ? parsed : '';
    },

    onMontoEditBlur(value) {
      const parsed = this.parseMontoLocale(value);
      this.editForm.monto = parsed != null ? parsed : '';
      this.montoEditFormateado = parsed != null ? this.formatMonto(parsed) : '';
    },

    onVentaManualMontoInput(value, field, formattedField) {
      // Mientras el usuario escribe se deja el texto tal cual (sin reformatear en cada tecla).
      this[formattedField] = value;
      const parsed = this.parseMontoLocale(value);
      this.nuevaVentaManual[field] = parsed != null ? parsed : '';
    },

    onVentaManualMontoBlur(value, field, formattedField) {
      const parsed = this.parseMontoLocale(value);
      this.nuevaVentaManual[field] = parsed != null ? parsed : '';
      this[formattedField] = parsed != null ? this.formatMonto(parsed) : '';
    },

    formatFecha(fecha) {
      if (!fecha) return '-';
      return new Date(fecha).toLocaleDateString('es-CL');
    },

    estadoFlujo(idEstado) {
      const estados = {
        1: 'Por Activar',
        2: 'Publicado',
        3: 'Adjudicado',
        4: 'Cancelado',
        5: 'Completado',
      };
      return estados[idEstado] || '-';
    },

    badgeClass(activo) {
      return activo
        ? 'badge badge-pill badge-success'
        : 'badge badge-pill badge-danger';
    },

    async verDetalle(venta) {
      this.ventaSeleccionada = venta;
      this.archivos = [];
      this.archivosSeleccionados = [];
      this.pagos = [];
      this.errorArchivos = '';
      this.errorPagos = '';
      this.modoEdicion = false;
      this.errorEdicionVenta = '';
      this.modalVisible = true;
      await Promise.all([
        this.cargarArchivos(venta.id),
        this.cargarTiposAdjunto(),
        this.cargarPagos(venta.id),
      ]);
    },

    onModalHidden() {
      this.modoEdicion = false;
      this.errorEdicionVenta = '';
    },

    activarEdicion() {
      if (!this.puedeEditar) return;
      this.editForm = {
        cantidad: this.ventaSeleccionada.cantidad,
        monto: this.ventaSeleccionada.monto,
        moneda: this.ventaSeleccionada.moneda || 'USD',
        fecha: this.ventaSeleccionada.fecha ? this.ventaSeleccionada.fecha.slice(0, 10) : '',
        observacion: this.ventaSeleccionada.observacion || '',
        idEstado: this.ventaSeleccionada.idEstado || 1,
      };
      this.montoEditFormateado = this.editForm.monto != null && this.editForm.monto !== ''
        ? this.formatMonto(this.editForm.monto)
        : '';
      this.errorEdicionVenta = '';
      this.modoEdicion = true;
    },

    cancelarEdicion() {
      this.modoEdicion = false;
      this.errorEdicionVenta = '';
    },

    async guardarVenta() {
      if (!this.puedeEditar) return;
      if (!this.editForm.cantidad || !this.editForm.monto || !this.editForm.fecha) {
        this.errorEdicionVenta = this.$t('campos_cantidad_monto_fecha');
        return;
      }
      const confirm = await Swal.fire({
        title: this.$t('seguro_guardar_cambios'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('guardar'),
        cancelButtonText: this.$t('cancelar'),
      });
      if (!confirm.isConfirmed) return;
      this.guardandoVenta = true;
      this.errorEdicionVenta = '';
      try {
        const idUsuario = Number(localStorage.getItem('user-id'));
        const payload = {
          idMaterial: this.ventaSeleccionada.idItem,
          idCliente: this.ventaSeleccionada.idEmpresa,
          cantidad: Number(this.editForm.cantidad),
          monto: Number(this.editForm.monto),
          moneda: this.editForm.moneda,
          fecha: this.editForm.fecha,
          idUsuarioCreacion: this.ventaSeleccionada.idUsuarioCreacion,
          observacion: this.editForm.observacion || '',
          idEstado: Number(this.editForm.idEstado),
        };
        await ventasService.actualizarVenta(this.ventaSeleccionada.id, payload, idUsuario);
        // Reflect changes in the selected object and table row
        Object.assign(this.ventaSeleccionada, {
          cantidad: payload.cantidad,
          monto: payload.monto,
          moneda: payload.moneda,
          fecha: this.editForm.fecha,
          observacion: payload.observacion,
          idEstado: payload.idEstado,
        });
        const idx = this.ventas.findIndex(v => v.id === this.ventaSeleccionada.id);
        if (idx >= 0) this.$set(this.ventas, idx, { ...this.ventas[idx], ...this.ventaSeleccionada });
        this.modoEdicion = false;
        Swal.fire({ icon: 'success', title: this.$t('transaccion_actualizada'), timer: 1800, showConfirmButton: false });
      } catch (e) {
        this.errorEdicionVenta = (e && e.response && e.response.data && e.response.data.message) || this.$t('error_actualizar_transaccion');
      } finally {
        this.guardandoVenta = false;
      }
    },

    ventaManualVacia() {
      return {
        sku: '',
        brandName: '',
        partName: '',
        detalles: '',
        partNumber: '',
        categoriaOrigen: null,
        cantidad: '',
        precio: '',
        monto: '',
        categoriaInexlink: null,
        idCondicion: null,
        unidadDeMedida: '',
        idEmpresaCompra: null,
        idEmpresaVende: null,
        moneda: 'USD',
        observacion: '',
        fechaVenta: '',
      };
    },

    async abrirIngresarVenta() {
      if (!this.puedeEditar) return;
      this.errorVentaManual = '';
      this.nuevaVentaManual = this.ventaManualVacia();
      this.montoVentaManualFormateado = '';
      this.precioLibroFormateado = '';
      this.modalIngresarVenta = true;
      await Promise.all([this.cargarCategorias(), this.cargarCondicionesVenta()]);
    },

    async cargarCategorias() {
      if (this.categorias.length) return;
      this.loadingCategorias = true;
      try {
        this.categorias = await materialService.getTaxonomias();
      } catch (e) {
        this.categorias = [];
      } finally {
        this.loadingCategorias = false;
      }
    },

    async cargarCondicionesVenta() {
      if (this.condiciones.length) return;
      this.loadingCondiciones = true;
      try {
        const data = await diccionarioService.getByTipo('CONDICION');
        this.condiciones = Array.isArray(data) ? data : [];
      } catch (e) {
        this.condiciones = [];
      } finally {
        this.loadingCondiciones = false;
      }
    },

    resetVentaManual() {
      this.nuevaVentaManual = this.ventaManualVacia();
      this.montoVentaManualFormateado = '';
      this.precioLibroFormateado = '';
      this.errorVentaManual = '';
    },

    async guardarVentaManual() {
      if (!this.puedeEditar) return;
      const v = this.nuevaVentaManual;
      if (!v.sku || !v.partName) {
        this.errorVentaManual = this.$t('campos_cantidad_monto_fecha') || 'SKU y Nombre Corto son requeridos.';
        return;
      }
      if (!v.cantidad || Number(v.cantidad) <= 0) {
        this.errorVentaManual = this.$t('error_cantidad_mayor_cero');
        return;
      }
      if (!v.monto || Number(v.monto) <= 0) {
        this.errorVentaManual = this.$t('error_monto_mayor_cero');
        return;
      }
      if (!v.categoriaInexlink || !v.idCondicion) {
        this.errorVentaManual = this.$t('datos_invalidos');
        return;
      }
      if (!v.idEmpresaCompra || !v.idEmpresaVende) {
        this.errorVentaManual = this.$t('error_debe_indicar_cliente');
        return;
      }
      if (!v.fechaVenta) {
        this.errorVentaManual = this.$t('error_fecha_venta_obligatoria');
        return;
      }

      const confirm = await Swal.fire({
        title: this.$t('seguro_guardar_cambios'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('guardar'),
        cancelButtonText: this.$t('cancelar'),
      });
      if (!confirm.isConfirmed) return;

      this.guardandoVentaManual = true;
      this.errorVentaManual = '';
      try {
        const idUsuario = Number(localStorage.getItem('user-id'));
        const payload = {
          sku: v.sku,
          brandName: v.brandName,
          partName: v.partName,
          detalles: v.detalles,
          partNumber: v.partNumber,
          categoriaOrigen: v.categoriaOrigen || '',
          cantidad: Number(v.cantidad),
          precio: String(v.precio || ''),
          monto: Number(v.monto),
          categoriaInexlink: v.categoriaInexlink,
          idCondicion: Number(v.idCondicion),
          unidadDeMedida: v.unidadDeMedida,
          idEmpresaCompra: Number(v.idEmpresaCompra),
          idEmpresaVende: Number(v.idEmpresaVende),
          moneda: v.moneda,
          observacion: v.observacion || '',
          fechaVenta: (v.fechaVenta || '').replace(/-/g, ''),
          idUsuario,
        };
        await ventasService.crearVentaManual(payload);
        this.modalIngresarVenta = false;
        Swal.fire({ icon: 'success', title: this.$t('venta_creada_ok'), timer: 1800, showConfirmButton: false });
        this.buscarVentas();
      } catch (e) {
        this.errorVentaManual = (e && e.response && e.response.data && e.response.data.message) || this.$t('error_registrar_venta');
      } finally {
        this.guardandoVentaManual = false;
      }
    },

    async cargarPagos(idVenta) {
      this.loadingPagos = true;
      try {
        const resp = await ventaPagoService.getPagosByVenta(idVenta);
        this.pagos = Array.isArray(resp) ? resp : [];
      } catch (e) {
        this.pagos = [];
      } finally {
        this.loadingPagos = false;
      }
    },

    abrirNuevoPago() {
      if (!this.puedeEditar) return;
      this.pagoEditandoId = null;
      this.nuevoPago = { fecha: '', moneda: 'USD', monto: '', observacion: '' };
      this.errorPagos = '';
      this.modalNuevoPago = true;
    },

    abrirEditarPago(pago) {
      if (!this.puedeEditar) return;
      this.pagoEditandoId = pago.id;
      this.nuevoPago = {
        fecha: pago.fecha ? pago.fecha.slice(0, 10) : '',
        moneda: pago.moneda,
        monto: pago.monto,
        observacion: pago.observacion || '',
      };
      this.errorPagos = '';
      this.modalNuevoPago = true;
    },

    async guardarPago() {
      if (!this.puedeEditar) return;
      if (!this.nuevoPago.fecha || !this.nuevoPago.monto) return;
      this.guardandoPago = true;
      this.errorPagos = '';
      try {
        const idUsuario = Number(localStorage.getItem('user-id'));
        const payload = {
          idVenta: this.ventaSeleccionada.id,
          fecha: this.nuevoPago.fecha,
          moneda: this.nuevoPago.moneda,
          monto: Number(this.nuevoPago.monto),
          observacion: this.nuevoPago.observacion || null,
          idUsuarioCreacion: idUsuario,
        };
        if (this.pagoEditandoId) {
          await ventaPagoService.editarPago(this.pagoEditandoId, payload);
        } else {
          await ventaPagoService.registrarPago(payload);
        }
        this.modalNuevoPago = false;
        this.resetNuevoPago();
        await this.cargarPagos(this.ventaSeleccionada.id);
      } catch (e) {
        this.errorPagos = (e && e.response && e.response.data && e.response.data.message) || 'Error al guardar el pago.';
      } finally {
        this.guardandoPago = false;
      }
    },

    resetNuevoPago() {
      this.nuevoPago = { fecha: '', moneda: 'USD', monto: '', observacion: '' };
      this.pagoEditandoId = null;
      this.errorPagos = '';
    },

    async cargarTiposAdjunto() {
      if (this.tiposAdjunto.length) return;
      try {
        const resp = await ventaTipoAdjuntoService.getAll();
        this.tiposAdjunto = Array.isArray(resp) ? resp : [];
      } catch (e) {
        this.tiposAdjunto = [];
      }
    },

    async cargarArchivos(idItem) {
      this.loadingArchivos = true;
      try {
        //idItem es el id de la venta, familia = 3 para ventas
        const resp = await archivoAdjuntoService.getArchivosByItem(idItem, 3);
        this.archivos = (resp && resp.data) ? resp.data : [];
      } catch (e) {
        this.archivos = [];
      } finally {
        this.loadingArchivos = false;
      }
    },

    async subirArchivos() {
      if (!this.puedeEditar) return;
      if (!this.archivoNuevo || !this.tipoAdjuntoSeleccionado) return;
      this.subiendoArchivos = true;
      this.errorArchivos = '';
      try {
        await archivoAdjuntoService.subirArchivos(
          3,
          1,
          this.ventaSeleccionada.id,
          [this.archivoNuevo],
          this.tipoAdjuntoSeleccionado,
          this.observacionNuevo
        );
        this.modalNuevoAdjunto = false;
        this.resetNuevoAdjunto();
        await this.cargarArchivos(this.ventaSeleccionada.id);
        Swal.fire({ icon: 'success', title: this.$t('archivo_subido') || 'Archivo subido correctamente', timer: 1800, showConfirmButton: false });
      } catch (e) {
        this.errorArchivos = (e && e.response && e.response.data && e.response.data.message) || 'Error al subir el archivo.';
      } finally {
        this.subiendoArchivos = false;
      }
    },

    resetNuevoAdjunto() {
      this.archivoNuevo = null;
      this.tipoAdjuntoSeleccionado = null;
      this.observacionNuevo = '';
      this.errorArchivos = '';
    },

    verDetalleArchivos(grupo) {
      this.procesoSeleccionado = grupo;
      this.errorDetalle = '';
      this.modalDetalleArchivos = true;
    },

    async eliminarArchivo(id, grupo = null) {
      if (!this.puedeEditar) return;
      const confirm = await Swal.fire({
        title: this.$t('esta_seguro') || '¿Estás seguro?',
        text: this.$t('eliminar_irreversible') || 'Esta acción es irreversible.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: this.$t('eliminar') || 'Eliminar',
        cancelButtonText: this.$t('cancelar') || 'Cancelar',
        reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;
      this.errorDetalle = '';
      try {
        await archivoAdjuntoService.eliminarArchivo(id);
        await this.cargarArchivos(this.ventaSeleccionada.id);
        if (grupo) {
          const actualizado = this.archivosAgrupados.find(g => g.idTipo === grupo.idTipo);
          this.procesoSeleccionado = actualizado || null;
          if (!actualizado || !actualizado.archivos.length) this.modalDetalleArchivos = false;
        }
      } catch (e) {
        this.errorDetalle = (e && e.response && e.response.data && e.response.data.message) || 'Error al eliminar el archivo.';
      }
    },

    nombreArchivo(url) {
      return url ? url.split('/').pop() : '-';
    },

    abrirAdjuntoPago(pago) {
      if (!this.puedeEditar) return;
      this.pagoSeleccionadoAdjunto = pago;
      this.archivoPago = null;
      this.comentarioPago = '';
      this.errorAdjuntoPago = '';
      this.modalAdjuntoPago = true;
    },

    async subirAdjuntoPago() {
      if (!this.puedeEditar) return;
      if (!this.archivoPago) return;
      this.subiendoAdjuntoPago = true;
      this.errorAdjuntoPago = '';
      try {
        await archivoAdjuntoService.subirArchivos(
          4,
          4,
          this.pagoSeleccionadoAdjunto.id,
          [this.archivoPago],
          null,
          this.comentarioPago
        );
        this.modalAdjuntoPago = false;
        this.resetAdjuntoPago();
        Swal.fire({ icon: 'success', title: this.$t('archivo_subido') || 'Archivo subido correctamente', timer: 1800, showConfirmButton: false });
      } catch (e) {
        this.errorAdjuntoPago = (e && e.response && e.response.data && e.response.data.message) || 'Error al subir el archivo.';
      } finally {
        this.subiendoAdjuntoPago = false;
      }
    },

    resetAdjuntoPago() {
      this.archivoPago = null;
      this.comentarioPago = '';
      this.errorAdjuntoPago = '';
      this.pagoSeleccionadoAdjunto = null;
    },

    async verAdjuntosPago(pago) {
      this.pagoSeleccionadoVer = pago;
      this.adjuntosPago = [];
      this.errorAdjuntosPagoVer = '';
      this.modalVerAdjuntosPago = true;
      this.loadingAdjuntosPago = true;
      try {
        const resp = await archivoAdjuntoService.getArchivosByItem(pago.id, 4);
        this.adjuntosPago = (resp && resp.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      } catch (e) {
        this.adjuntosPago = [];
      } finally {
        this.loadingAdjuntosPago = false;
      }
    },

    async eliminarAdjuntoPago(id) {
      if (!this.puedeEditar) return;
      const confirm = await Swal.fire({
        title: this.$t('esta_seguro') || '¿Estás seguro?',
        text: this.$t('eliminar_irreversible') || 'Esta acción es irreversible.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: this.$t('eliminar') || 'Eliminar',
        cancelButtonText: this.$t('cancelar') || 'Cancelar',
        reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;
      this.errorAdjuntosPagoVer = '';
      try {
        await archivoAdjuntoService.eliminarArchivo(id);
        this.adjuntosPago = this.adjuntosPago.filter(a => a.id !== id);
      } catch (e) {
        this.errorAdjuntosPagoVer = (e && e.response && e.response.data && e.response.data.message) || 'Error al eliminar el archivo.';
      }
    },

    exportarExcel() {
      const hID              = 'ID';
      const hDesc            = this.$t('descripcion');
      const hVendedor        = this.$t('vendedor');
      const hComprador       = this.$t('comprador');
      const hUOM             = this.$t('uom');
      const hCantidad        = this.$t('cantidad_label');
      const hMonto           = this.$t('monto');
      const hMoneda          = this.$t('moneda');
      const hFechaVenta      = this.$t('fecha_venta');
      const hFechaPublicacion= this.$t('fecha_publicacion');
      const hEstadoFlujo     = this.$t('estado_flujo');
      const hObservacion     = this.$t('observacion');

      const filas = this.ventasOrdenadas.map(v => ({
        [hID]:              v.id,
        [hDesc]:            v.partName || '',
        [hVendedor]:        v.nombreEmpresaVende || '',
        [hComprador]:       v.nombreEmpresaCompra || '',
        [hUOM]:             v.unidadDeMedida || '',
        [hCantidad]:        v.cantidad,
        [hMonto]:           v.monto,
        [hMoneda]:          v.moneda || '',
        [hFechaVenta]:      this.formatFecha(v.fecha),
        [hFechaPublicacion]:this.formatFecha(v.fechaPublicacion),
        [hEstadoFlujo]:     this.estadoFlujo(v.idEstado),
        [hObservacion]:     v.observacion || '',
      }));
      const ws = XLSX.utils.json_to_sheet(filas);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
      XLSX.writeFile(wb, `ventas_${this.fechaInicio}_${this.fechaFin}.xlsx`);
    },
  },
};
</script>

<style scoped>
.divisor {
  font-size: 16px;
  font-weight: 400;
  color: #fff;
  padding: 15px;
  background: #6172e9;
  border-radius: 10px;
}
.form-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #525f7f;
  margin-bottom: 4px;
  display: block;
}
.card {
  border-radius: 10px;
  overflow: hidden;
}

/* ===== Tabla con cabecera inmóvil ===== */
.tabla-scroll-fixed {
  overflow-x: auto;
  position: relative;
  font-size: 0.8rem;
}

.thead-sticky {
  position: relative;
  z-index: 2;
  background: #f8f9fa;
}

.th-ordenable {
  cursor: pointer;
  user-select: none;
  min-width: 90px;
  vertical-align: top;
  white-space: nowrap;
}
.th-ordenable:hover {
  background-color: #e9ecef !important;
}

.th-search-input {
  width: 100%;
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  margin-bottom: 3px;
  display: block;
  font-weight: normal;
}

.th-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.tabla-scroll-fixed .table th,
.tabla-scroll-fixed .table td {
  font-size: 12px;
}

/* ===== Botones de scroll ===== */
.btn-scroll-nav {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ced4da;
  background: #f8f9fa;
  color: #495057;
  border-radius: 6px;
}
.btn-scroll-nav:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

/* ===== Barra flotante de paginación ===== */
.pagination-floating-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}

.detalle-modal {
  padding: 8px 0;
}
.detalle-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.detalle-item:last-child {
  border-bottom: none;
}
.detalle-label {
  font-weight: 600;
  color: #525f7f;
  font-size: 0.85rem;
  flex: 0 0 45%;
}
.detalle-valor {
  color: #32325d;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
}
.seccion-col {
  padding-top: 4px;
}
.seccion-col--border {
  border-left: 1px solid #e9ecef;
}
.archivos-seccion {
  padding-top: 4px;
}
.archivos-titulo {
  font-weight: 600;
  color: #525f7f;
  margin-bottom: 12px;
}
.archivo-link {
  font-size: 0.88rem;
  color: #6172e9;
  word-break: break-all;
}
.archivo-link:hover {
  text-decoration: underline;
}
</style>
